<template>
  <a
    class="base-link"
    :class="[
      `base-link--size-${size}`,
      `base-link--color-${color}`,
      `base-link--style-${buttonStyle}`,
      {
        'base-link--rounded': rounded,
      },
    ]"
  >
    <slot />
  </a>
</template>

<script>
  export default {
    name: 'BaseLink',
    props: {
      type: {
        type: String,
        required: false,
        default: 'button',
        validator: (value) => {
          const TYPES = ['button', 'submit', 'reset'];
          return TYPES.includes(value);
        },
      },
      size: {
        type: String,
        required: false,
        default: 'md',
        validator: (value) => {
          const SIZES = ['xs', 'sm', 'md', 'lg', 'xl', 'flat'];
          return SIZES.includes(value);
        },
      },
      color: {
        type: String,
        required: false,
        default: 'primary',
        validator: (value) => {
          const COLORS = [
            'primary',
            'secondary',
            'cta',
            'success',
            'warning',
            'danger',
            'grey',
            'dark-grey',
            'green',
            'green-mid',
            'white',
          ];
          return COLORS.includes(value);
        },
      },
      buttonStyle: {
        type: String,
        required: false,
        default: 'normal',
        validator: (value) => {
          const STYLES = ['normal', 'flat'];
          return STYLES.includes(value);
        },
      },
      rounded: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
  };
</script>

<style lang="scss" scoped>
  .base-link {
    cursor: pointer;
    border-radius: 4px;
    box-shadow: 0px 3px 5px rgba($black, 0.25);

    font-family: $font-secondary;

    font-weight: bold;
    line-height: 34px;
    outline: 0;
    overflow: hidden;

    position: relative;
    text-decoration: none;
    text-transform: uppercase;
    white-space: nowrap;
    transition:
      background-color $default-transition-time,
      color $default-transition-time,
      border-radius $default-transition-time;

    // min-width: fit-content;

    &--rounded {
      border-radius: 100%;
    }

    &--style-flat {
      box-shadow: none;
    }

    &:hover {
      border-radius: 10px;

      &:disabled {
        border-radius: 4px;
        background-color: #cccccc;
        color: #333333;
      }
    }

    &:disabled {
      cursor: not-allowed;
      background-color: #cccccc;
      color: #333333;
      box-shadow: none;
    }

    &:focus,
    &:active {
      box-shadow: none;
      border-radius: 10px;

      &:disabled {
        border-radius: 4px;
      }
    }

    &--size-xs {
      padding: 2px 5px;
      font-size: 10px;
    }
    &--size-sm {
      padding: 5px 10px;
      font-size: 12px;
    }
    &--size-md {
      padding: 8px 24px;
      font-size: 14px;
    }
    &--size-lg {
      padding: 5px 15px;
      font-size: 12px;
    }
    &--size-xl {
      padding: 5px 15px;
      font-size: 12px;
    }
    &--size-flat {
      padding: 1px 16px;
      font-size: 10px;
    }

    &--color-primary {
      background-color: $green-mid;
      color: $white;

      &:hover {
        background-color: $green-btn-hover;
      }

      &:focus,
      &:active {
        background-color: $green-btn-hover;
        color: $white;
        box-shadow: none;
        border-radius: 10px;

        &:disabled {
          border-radius: 4px;
          background-color: #cccccc;
          color: #333333;
        }
      }
    }
    &--color-green {
      background-color: $green;
      color: $white;

      &:hover {
        background-color: darken($green, 5%);
      }

      &:focus,
      &:active {
        background-color: darken($green, 10%);
        color: $white;
        box-shadow: none;
        border-radius: 10px;

        &:disabled {
          border-radius: 4px;
          background-color: #cccccc;
          color: #333333;
        }
      }
    }
    &--color-green-mid {
      background-color: $green-mid;
      color: $white;

      &:hover {
        background-color: darken($green-mid, 5%);
      }

      &:focus,
      &:active {
        background-color: darken($green-mid, 10%);
        color: $white;
        box-shadow: none;
        border-radius: 10px;

        &:disabled {
          border-radius: 4px;
          background-color: #cccccc;
          color: #333333;
        }
      }
    }
    &--color-secondary {
      background-color: #111111;
      color: $white;

      &:focus,
      &:active {
        background-color: $green-btn-hover;
        color: $white;
        box-shadow: none;
        border-radius: 10px;

        &:disabled {
          border-radius: 4px;
          background-color: #cccccc;
          color: #333333;
        }
      }
    }
    &--color-cta {
      background-color: #111111;
      color: $white;
    }
    &--color-success {
      background-color: #111111;
      color: $white;
    }
    &--color-warning {
      background-color: #111111;
      color: $white;
    }
    &--color-danger {
      background-color: #111111;
      color: $white;
    }
    &--color-grey {
      background-color: #a7a9ab52;
      color: #111;

      &:focus,
      &:active {
        background-color: #a7a9ab28;
        color: #111;

        &:disabled {
          background-color: #cccccc;
          color: #333333;
        }
      }
    }
    &--color-dark-grey {
      background-color: var(--itr-grey-mid);
      color: var(--itr-white);

      &:disabled {
        background-color: var(--itr-grey);
        color: var(--itr-white);
      }

      &:focus,
      &:active,
      &:hover {
        background-color: var(--itr-grey);
        color: var(--itr-white);
      }
    }
    &--color-white {
      background-color: #ffffff;
      color: #111;

      &:focus,
      &:active {
        background-color: #f1f2f3;
        color: #111;

        &:disabled {
          background-color: #cccccc;
          color: #333333;
        }
      }
    }
  }
</style>
